(() => {
  'use strict';

  const API = 'https://notify.poleward.online/api/birthday';
  const STORAGE_KEY = 'poleward.birthday-gift.v1';
  const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
  const INVOICE_HOSTS = new Set(['poleward.online', 'poleward-duandian.myshopify.com', 'polewardoffical.myshopify.com']);
  const CAPABILITY = /^([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})\.([a-f0-9]{64})$/i;

  function parseCapability(value) {
    const match = typeof value === 'string' && CAPABILITY.exec(value);
    return match ? { id: match[1].toLowerCase(), token: match[2].toLowerCase() } : null;
  }

  function removeStored(storage) {
    try { storage.removeItem(STORAGE_KEY); } catch {}
  }

  function saveCapability(storage, capability) {
    try { storage.setItem(STORAGE_KEY, JSON.stringify(capability)); } catch {}
  }

  function readCapability(location, history, storage, now) {
    const fragment = new URLSearchParams(location.hash.slice(1));
    if (fragment.has('aurora')) {
      const values = fragment.getAll('aurora');
      const parsed = values.length === 1 ? parseCapability(values[0]) : null;
      fragment.delete('aurora');
      const remaining = fragment.toString();
      try { history.replaceState(history.state, '', location.pathname + location.search + (remaining ? '#' + remaining : '')); } catch {}
      removeStored(storage);
      if (!parsed) return null;
      const capability = { ...parsed, savedAt: now, expiresAt: now + MAX_AGE };
      saveCapability(storage, capability);
      return capability;
    }
    try {
      const stored = JSON.parse(storage.getItem(STORAGE_KEY));
      const parsed = stored && parseCapability(stored.id + '.' + stored.token);
      if (!parsed || !Number.isSafeInteger(stored.savedAt) || stored.savedAt > now
        || !Number.isSafeInteger(stored.expiresAt) || stored.expiresAt <= now
        || stored.expiresAt > stored.savedAt + MAX_AGE) {
        removeStored(storage);
        return null;
      }
      return { ...parsed, savedAt: stored.savedAt, expiresAt: stored.expiresAt };
    } catch { removeStored(storage); return null; }
  }

  function cartEndpoint(win) {
    const candidate = win.Shopify?.routes?.root
      ? win.Shopify.routes.root + 'cart.js'
      : (win.routes?.cart_url || '/cart').replace(/\/$/, '') + '.js';
    try {
      const url = new URL(candidate, win.location.origin);
      if (url.origin !== win.location.origin || !/\/cart\.js$/.test(url.pathname) || url.search || url.hash) return null;
      return url.pathname;
    } catch { return null; }
  }

  function invoiceUrl(value) {
    if (typeof value !== 'string' || value.length > 4096 || /[\s\\]/.test(value)) return null;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && INVOICE_HOSTS.has(url.hostname)
        && !url.username && !url.password && !url.port
        && /^\/(?:(?:\d+\/)?invoices\/|checkouts\/)/.test(url.pathname) ? url.href : null;
    } catch { return null; }
  }

  function cartPayload(cart, capability, currency) {
    if (cart?.currency !== currency) throw new Error('currency');
    if (!Array.isArray(cart.items) || cart.items.length === 0) throw new Error('empty');
    if (cart.items.length > 250) throw new Error('cart');
    const items = cart.items.map((item) => {
      const id = String(item.variant_id);
      if (!/^[1-9]\d{0,19}$/.test(id) || (typeof item.variant_id === 'number' && !Number.isSafeInteger(item.variant_id))
        || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 999
        || item.selling_plan_allocation || (item.properties && Object.keys(item.properties).length)) throw new Error('cart');
      return { variantId: 'gid://shopify/ProductVariant/' + id, quantity: item.quantity };
    });
    return JSON.stringify({ entitlementId: capability.id, token: capability.token, items, currency });
  }

  async function pollCheckout(fetchJSON, body, { now, wait }) {
    const deadline = now() + 90000;
    for (let attempt = 0; attempt < 45 && now() < deadline; attempt++) {
      const result = await fetchJSON(API + '/checkout', {
        method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body
      }, Math.min(12000, deadline - now()));
      if (result?.status !== 'pending') return result;
      const delay = Math.min(8000, Math.max(1000, Number(result.retryAfterMs) || 2000));
      if (now() + delay >= deadline) break;
      await wait(delay);
    }
    return { status: 'pending' };
  }

  function createFlow({ capability, storage, fetchJSON, cartUrl, now, wait, render, navigate }) {
    let offer = null;
    let busy = false;
    let body = null;
    let destination = null;
    let retryOffer = false;
    let state = { status: 'Checking your birthday gift…', action: '', busy: true, offer: null };
    const update = (next) => { state = { ...state, ...next }; render(state); };
    const terminal = (status) => {
      destination = null;
      retryOffer = false;
      const messages = {
        expired: 'This birthday gift has expired',
        offline: 'Birthday gifts are not available yet',
        conflict: 'Your birthday gift checkout is already in progress',
        unavailable: 'This birthday gift is unavailable'
      };
      if (status === 'expired' || status === 'unavailable') removeStored(storage);
      update({ status: messages[status] || messages.unavailable, action: '', busy: false });
    };
    const ready = (value) => {
      destination = invoiceUrl(value);
      if (!destination) { terminal('unavailable'); return; }
      update({ status: 'Your birthday gift is ready', action: 'Continue to checkout', busy: false });
    };

    async function loadOffer() {
      if (busy) return;
      busy = true;
      update({ status: 'Checking your birthday gift…', action: '', busy: true });
      try {
        const result = await fetchJSON(API + '/offer?id=' + encodeURIComponent(capability.id), {
          method: 'GET', credentials: 'omit', headers: { Authorization: 'Bearer ' + capability.token, Accept: 'application/json' }
        });
        if (result?.status !== 'available') { terminal(result?.status); return; }
        if (!['GBP', 'USD'].includes(result.currency) || !Number.isSafeInteger(result.expiresAt)
          || ![15, 20].every((tier) => Number.isSafeInteger(result.minimums?.[tier]) && result.minimums[tier] > 0)) {
          terminal('unavailable'); return;
        }
        if (result.expiresAt <= now()) { terminal('expired'); return; }
        offer = { currency: result.currency, expiresAt: result.expiresAt, minimums: result.minimums };
        capability.expiresAt = Math.min(result.expiresAt, capability.savedAt + MAX_AGE);
        saveCapability(storage, capability);
        retryOffer = false;
        update({ offer, status: '', action: 'Use your birthday gift', busy: false });
        if (result.checkoutStatus === 'ready') ready(result.invoiceUrl);
        else if (result.checkoutStatus === 'conflict') terminal('conflict');
      } catch {
        retryOffer = true;
        update({ status: 'Unable to load your birthday gift', action: 'Try again', busy: false });
      } finally { busy = false; }
    }

    async function activate() {
      if (busy) return;
      if (offer && offer.expiresAt <= now()) { terminal('expired'); return; }
      if (destination) { navigate(destination); return; }
      if (retryOffer) { await loadOffer(); return; }
      if (!offer || !state.action) return;
      busy = true;
      update({ status: 'Preparing your birthday gift…', busy: true });
      try {
        if (!body) {
          if (!cartUrl) throw new Error('cart');
          const cart = await fetchJSON(cartUrl, { method: 'GET', credentials: 'same-origin', headers: { Accept: 'application/json' } });
          body = cartPayload(cart, capability, offer.currency);
        }
        const result = await pollCheckout(fetchJSON, body, { now, wait });
        if (result?.status === 'ready') ready(result.invoiceUrl);
        else if (result?.status === 'pending') update({ status: 'Your birthday gift is still being prepared', action: 'Try again', busy: false });
        else if (result?.status === 'currency_mismatch') update({ status: 'Switch to ' + offer.currency + ' to use your gift', action: 'Try again', busy: false });
        else if (result?.status === 'minimum_not_met') update({ status: 'Your cart has not reached the gift minimum', action: 'Try again', busy: false });
        else terminal(result?.status);
      } catch (error) {
        const messages = { currency: 'Switch to ' + offer.currency + ' to use your gift', empty: 'Add an item to use your birthday gift', cart: 'This cart cannot use the birthday gift checkout' };
        update({ status: messages[error?.message] || 'Unable to prepare your birthday gift', action: 'Try again', busy: false });
      } finally { busy = false; }
    }

    function refreshTime() {
      if (offer && !busy && offer.expiresAt <= now()) terminal('expired');
      else render(state);
    }
    return { loadOffer, activate, refreshTime };
  }

  async function jsonRequest(win, url, options, timeout = 12000) {
    const controller = new win.AbortController();
    const timer = win.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await win.fetch(url, { ...options, signal: controller.signal, cache: 'no-store', redirect: 'error', referrerPolicy: 'strict-origin-when-cross-origin' });
      const result = await response.json();
      if (!response.ok && !['unavailable', 'expired', 'offline', 'conflict', 'currency_mismatch', 'minimum_not_met'].includes(result?.status)) throw new Error('network');
      return result;
    } finally { win.clearTimeout(timer); }
  }

  function boot(win) {
    const doc = win.document;
    if (!/\/cart\/?$/.test(win.location.pathname) || doc.getElementById('poleward-birthday-gift')) return null;
    let storage;
    try { storage = win.sessionStorage; } catch { storage = null; }
    const now = () => Date.now();
    const capability = readCapability(win.location, win.history, storage, now());
    if (!capability) return null;
    const stylesheet = doc.currentScript?.dataset.stylesheet;
    if (stylesheet) {
      const link = doc.createElement('link');
      link.rel = 'stylesheet';
      link.href = stylesheet;
      doc.head.append(link);
    }
    const panel = doc.createElement('section');
    panel.id = 'poleward-birthday-gift';
    panel.className = 'poleward-birthday-gift';
    panel.setAttribute('aria-labelledby', 'poleward-birthday-gift-title');
    const heading = doc.createElement('h2');
    heading.id = 'poleward-birthday-gift-title';
    heading.className = 'poleward-birthday-gift__title';
    heading.textContent = 'Your birthday gift';
    const terms = doc.createElement('div');
    terms.className = 'poleward-birthday-gift__terms';
    const countdown = doc.createElement('span');
    countdown.className = 'poleward-birthday-gift__time';
    const status = doc.createElement('div');
    status.className = 'poleward-birthday-gift__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'poleward-birthday-gift__button';
    panel.append(heading, terms, countdown, status, button);
    let termsFor = null;
    const render = (state) => {
      if (status.textContent !== state.status) status.textContent = state.status;
      status.hidden = !state.status;
      button.hidden = !state.action;
      button.disabled = state.busy;
      button.textContent = state.busy && state.action ? 'Preparing…' : state.action;
      panel.setAttribute('aria-busy', String(state.busy));
      terms.hidden = !state.offer;
      countdown.hidden = !state.offer;
      if (state.offer) {
        if (termsFor !== state.offer) {
          termsFor = state.offer;
          const money = new Intl.NumberFormat('en-GB', { style: 'currency', currency: state.offer.currency, maximumFractionDigits: 0 });
          terms.textContent = [15, 20].map((tier) => tier + '% off ' + money.format(state.offer.minimums[tier] / 100) + '+').join(' · ');
        }
        const minutes = Math.max(0, Math.ceil((state.offer.expiresAt - now()) / 60000));
        countdown.textContent = minutes > 1440 ? Math.ceil(minutes / 1440) + ' days to claim' : minutes > 60 ? Math.ceil(minutes / 60) + ' hours to claim' : minutes > 0 ? minutes + ' min to claim' : 'Expired';
      }
    };
    const flow = createFlow({ capability, storage, cartUrl: cartEndpoint(win), now,
      wait: (ms) => new Promise((resolve) => win.setTimeout(resolve, ms)),
      fetchJSON: (url, options, timeout) => jsonRequest(win, url, options, timeout),
      render, navigate: (url) => win.location.assign(url) });
    button.addEventListener('click', () => { void flow.activate(); });
    const attach = () => {
      const footer = doc.getElementById('main-cart-footer');
      const target = footer && !footer.classList.contains('is-empty') ? footer.querySelector('.cart__ctas') : null;
      if (target?.parentNode) {
        if (panel.parentNode !== target.parentNode || panel.nextElementSibling !== target) target.parentNode.insertBefore(panel, target);
      } else {
        const fallback = doc.querySelector('.cart__warnings') || doc.querySelector('form[action$="/cart"]');
        if (fallback && panel.parentNode !== fallback) fallback.append(panel);
      }
    };
    attach();
    const observer = new win.MutationObserver(attach);
    observer.observe(doc.getElementById('MainContent') || doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    let interval = win.setInterval(() => flow.refreshTime(), 60000);
    win.addEventListener('pagehide', () => { observer.disconnect(); win.clearInterval(interval); });
    win.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      attach();
      flow.refreshTime();
      observer.observe(doc.getElementById('MainContent') || doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
      interval = win.setInterval(() => flow.refreshTime(), 60000);
    });
    void flow.loadOffer();
    return flow;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { parseCapability, readCapability, cartEndpoint, invoiceUrl, cartPayload, pollCheckout, createFlow, boot };
  } else boot(window);
})();
